import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Building,
  MapPin,
  Clock,
  Briefcase,
  AlertCircle,
  CheckCircle,
  XCircle,
  Users,
  Globe,
  Eye,
  Copy,
  Send,
  X,
  Info,
  FileText,
  Calendar,
  Target,
  Check,
  MoreVertical,
  Filter,
  Loader2
} from 'lucide-react';
import { toast } from 'react-toastify';

import { offersAPI } from '../../utils/api';
import { getErrorMessage } from '../../utils/errorTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import useConfirm from '../../hooks/useConfirm';

const OffersPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { confirm, confirmProps } = useConfirm();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, open: 0, closed: 0, cancelled: 0 });

  const offersQuery = useQuery(
    ['offers', { statusFilter, searchTerm }],
    async () => {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      const response = await offersAPI.getAll(params);
      return response.data;
    },
    {
      keepPreviousData: true,
      onError: (error) => {
        toast.error(getErrorMessage(error, t, t('offers.errors.loadFailed', 'Erreur lors du chargement des offres')));
      }
    }
  );

  const statsQuery = useQuery(
    ['offersStats', user?.id],
    async () => {
      const response = await offersAPI.getStats();
      return response.data;
    },
    {
      onError: () => {
        // Silent failure for stats
      }
    }
  );

  const offers = offersQuery.data?.offers || [];
  const loading = offersQuery.isLoading;

  useEffect(() => {
    if (statsQuery.data) {
      const data = statsQuery.data || {};
      const byStatus = data.by_status || data.byStatus || {};
      setStats({
        total: Number(data.total) || 0,
        open: Number(byStatus.open) || 0,
        closed: Number(byStatus.closed) || 0,
        cancelled: Number(byStatus.cancelled) || 0
      });
    }
  }, [statsQuery.data]);

  const activeFilters = React.useMemo(() => {
    const filters = [];
    if (searchTerm.trim()) {
      filters.push({
        key: 'search',
        label: `${t('offers.filters.search', 'Recherche')}: ${searchTerm.trim()}`,
        onClear: () => setSearchTerm('')
      });
    }
    if (statusFilter !== 'all') {
      filters.push({
        key: 'status',
        label: `${t('offers.filters.status', 'Statut')}: ${t(`offers.filters.${statusFilter}`, statusFilter)}`,
        onClear: () => setStatusFilter('all')
      });
    }
    return filters;
  }, [searchTerm, statusFilter, t]);

  // Handle delete
  const handleDelete = async (offerId) => {
    const result = await confirm({
      title: t('offers.deleteModal.title', 'Supprimer l\'offre'),
      message: t('offers.deleteModal.message', 'Êtes-vous sûr de vouloir supprimer cette offre ?\n\nCette action supprimera également tous les résultats d\'analyse associés.'),
      type: 'danger',
      confirmText: t('offers.deleteModal.confirm', 'Supprimer'),
      cancelText: t('offers.deleteModal.cancel', 'Annuler')
    });

    if (!result) return;

    try {
      await offersAPI.delete(offerId);
      toast.success(t('offers.messages.deleteSuccess', 'Offre supprimée avec succès'));
      queryClient.invalidateQueries('offers');
      queryClient.invalidateQueries(['offersStats', user?.id]);
    } catch (error) {
      toast.error(t('offers.errors.deleteFailed', 'Erreur lors de la suppression'));
      console.error('Error deleting offer:', error);
    }
  };

  // Handle edit
  const handleEdit = (offer) => {
    setSelectedOffer(offer);
    setShowEditModal(true);
  };

  // Handle publish
  const handlePublish = async (offer) => {
    try {
      const response = await offersAPI.publish(offer.id);
      const data = response.data;
      
      // Copier l'URL dans le presse-papier
      const fullUrl = data.full_url;
      navigator.clipboard.writeText(fullUrl);
      
      toast.success(
        <div>
          {t('offers.messages.publishSuccess', 'Offre publiée avec succès !')}<br/>
          <small>{t('offers.messages.urlCopied', 'URL copiée dans le presse-papier')}</small>
        </div>
      );
      
      queryClient.invalidateQueries('offers');
      queryClient.invalidateQueries(['offersStats', user?.id]);
    } catch (error) {
      toast.error(getErrorMessage(error, t, t('offers.errors.publishFailed', 'Erreur lors de la publication')));
    }
  };

  // Handle unpublish
  const handleUnpublish = async (offer) => {
    const result = await confirm({
      title: t('offers.unpublishModal.title', 'Retirer de la publication'),
      message: t('offers.unpublishModal.message', 'Êtes-vous sûr de vouloir retirer cette offre de la publication ?\n\nLe lien public ne sera plus accessible aux candidats.'),
      type: 'warning',
      confirmText: t('offers.unpublishModal.confirm', 'Retirer'),
      cancelText: t('offers.unpublishModal.cancel', 'Annuler')
    });
    
    if (result) {
      try {
        await offersAPI.unpublish(offer.id);
        toast.success(t('offers.messages.unpublishSuccess', 'Offre retirée de la publication'));
        queryClient.invalidateQueries('offers');
        queryClient.invalidateQueries(['offersStats', user?.id]);
      } catch (error) {
        toast.error(getErrorMessage(error, t, t('offers.errors.unpublishFailed', 'Erreur lors de la dépublication')));
      }
    }
  };

  // Copy public URL
  const copyPublicUrl = (offer) => {
    const fullUrl = `${window.location.origin}/jobs/${offer.public_url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success(t('offers.messages.urlCopied', 'URL copiée dans le presse-papier'));
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      open: { icon: CheckCircle, color: 'text-green-600 bg-green-100', label: t('offers.status.open', 'Ouverte') },
      closed: { icon: XCircle, color: 'text-gray-600 bg-gray-100', label: t('offers.status.closed', 'Fermée') },
      cancelled: { icon: AlertCircle, color: 'text-red-600 bg-red-100', label: t('offers.status.cancelled', 'Annulée') }
    };

    const config = statusConfig[status] || statusConfig.open;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  // Offer Card Component
  const OfferCard = ({ offer }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setShowDropdown(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-shadow duration-200"
      >
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 truncate pr-2">{offer.title}</h3>
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mb-3">
              {offer.client && (
                <div className="flex items-center">
                  <Building className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                  <span className="truncate max-w-[150px]">{offer.client}</span>
                </div>
              )}
              {offer.location && (
                <div className="flex items-center">
                  <MapPin className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                  <span className="truncate max-w-[150px]">{offer.location}</span>
                </div>
              )}
              {offer.duration && (
                <div className="flex items-center">
                  <Clock className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                  <span className="truncate max-w-[100px]">{offer.duration}</span>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <StatusBadge status={offer.status} />
              {offer.is_published && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
                  <Globe className="w-3 h-3 mr-1" />
                  {t('offers.labels.published', 'Publié')}
                </span>
              )}
            </div>
          </div>

          {/* Actions desktop - visible sur écrans moyens et grands */}
          <div className="hidden sm:flex items-center gap-1 flex-wrap justify-end ml-4">
            {/* Boutons de publication */}
            {offer.status === 'open' && (
              <>
                {offer.is_published ? (
                  <>
                    <button
                      onClick={() => copyPublicUrl(offer)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                      title={t('offers.actions.copyUrl', 'Copier l\'URL')}
                      aria-label={t('offers.actions.copyUrl')}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleUnpublish(offer)}
                      className="p-1.5 text-gray-400 hover:text-orange-600 rounded-md hover:bg-orange-50 transition-colors"
                      title={t('offers.actions.unpublish', 'Retirer de la publication')}
                      aria-label={t('offers.actions.unpublish')}
                    >
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handlePublish(offer)}
                    className="p-1.5 text-gray-400 hover:text-green-600 rounded-md hover:bg-green-50 transition-colors"
                    title={t('offers.actions.publish', 'Publier l\'offre')}
                    aria-label={t('offers.actions.publish')}
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                )}
              </>
            )}

            <button
              onClick={() => handleEdit(offer)}
              className="p-1.5 text-gray-400 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors"
              title={t('offers.actions.edit', 'Modifier')}
              aria-label={t('offers.actions.edit')}
            >
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleDelete(offer.id)}
              className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors"
              title={t('offers.actions.delete', 'Supprimer')}
              aria-label={t('offers.actions.delete')}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Actions mobile - menu dropdown */}
          <div className="relative sm:hidden" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-50 transition-colors"
              aria-label={t('common.more')}
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {/* Dropdown menu */}
            <AnimatePresence>
              {showDropdown && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
                >
                  <div className="py-1">
                    {offer.status === 'open' && (
                      <>
                        {offer.is_published ? (
                          <>
                            <button
                              onClick={() => {
                                copyPublicUrl(offer);
                                setShowDropdown(false);
                              }}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              {t('offers.actions.copyUrl', 'Copier l\'URL')}
                            </button>
                            <button
                              onClick={() => {
                                handleUnpublish(offer);
                                setShowDropdown(false);
                              }}
                              className="w-full flex items-center px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              {t('offers.actions.unpublish', 'Dépublier')}
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => {
                              handlePublish(offer);
                              setShowDropdown(false);
                            }}
                            className="w-full flex items-center px-4 py-2 text-sm text-green-600 hover:bg-green-50"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            {t('offers.actions.publish', 'Publier')}
                          </button>
                        )}
                        <div className="border-t border-gray-100"></div>
                      </>
                    )}

                    <button
                      onClick={() => {
                        handleEdit(offer);
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      {t('offers.actions.edit', 'Modifier')}
                    </button>
                    <button
                      onClick={() => {
                        handleDelete(offer.id);
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t('offers.actions.delete', 'Supprimer')}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {offer.required_skills && (
          <div className="mb-3">
            <div className="text-xs text-gray-600 mb-1">{t('offers.labels.requiredSkills', 'Compétences requises')} :</div>
            <div className="flex flex-wrap gap-1">
              {offer.required_skills.split(',').slice(0, 5).map((skill, index) => (
                <span
                  key={index}
                  className="inline-block px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded"
                >
                  {skill.trim()}
                </span>
              ))}
              {offer.required_skills.split(',').length > 5 && (
                <span className="inline-block px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                  +{offer.required_skills.split(',').length - 5}
                </span>
              )}
            </div>
          </div>
        )}

        {offer.full_description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">
            {offer.full_description}
          </p>
        )}

        <div className="border-t pt-2 mt-auto">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
            <div className="flex items-center gap-3">
              <span>
                {t('offers.labels.createdOn', 'Créée le')}{' '}
                {new Date(offer.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
              </span>
              {offer.is_published && (
                <div className="flex items-center text-blue-600">
                  <Globe className="w-3 h-3 mr-1" />
                  <span>{t('offers.labels.published', 'Publiée')}</span>
                </div>
              )}
            </div>
            {offer.is_published && offer.views_count > 0 && (
              <div className="flex items-center text-gray-500">
                <Eye className="w-3 h-3 mr-1" />
                <span>
                  {offer.views_count} {t('offers.labels.view', 'vue')}
                  {offer.views_count > 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <Briefcase className="w-6 h-6 text-indigo-600" />
          <h1 className="text-2xl font-semibold text-gray-900">{t('offers.title', 'Gestion des Offres')}</h1>
          {user?.role === 'rh' && (
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-700 rounded-full">
              <Users className="w-3 h-3 mr-1" />
              {t('offers.labels.team', 'Équipe')}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600">
          {user?.role === 'admin' 
            ? t('offers.subtitle.admin', 'Gérez les offres de votre équipe') 
            : t('offers.subtitle.hr', 'Offres partagées avec votre équipe')
          }
        </p>
        <div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            {t('offers.actions.createNew', 'Nouvelle offre')}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500">{t('offers.stats.total', 'Total')}</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
            </div>
            <Briefcase className="h-8 w-8 text-indigo-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500">{t('offers.stats.open', 'Ouvertes')}</p>
              <p className="text-2xl font-semibold text-emerald-600">{stats.open}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-emerald-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500">{t('offers.stats.closed', 'Fermées')}</p>
              <p className="text-2xl font-semibold text-gray-600">{stats.closed}</p>
            </div>
            <XCircle className="h-8 w-8 text-gray-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500">{t('offers.stats.cancelled', 'Annulées')}</p>
              <p className="text-2xl font-semibold text-red-600">{stats.cancelled}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <h2 className="text-sm font-semibold text-gray-900">{t('offers.filters.title', 'Filtres')}</h2>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder={t('offers.search.placeholder', 'Rechercher par titre, client, ou compétences...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-9 py-2 text-sm"
              aria-label={t('offers.search.placeholder')}
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-9 py-2 text-sm"
              aria-label={t('offers.filters.allStatuses')}
            >
              <option value="all">{t('offers.filters.allStatuses', 'Tous les statuts')}</option>
              <option value="open">{t('offers.filters.open', 'Ouvertes')}</option>
              <option value="closed">{t('offers.filters.closed', 'Fermées')}</option>
              <option value="cancelled">{t('offers.filters.cancelled', 'Annulées')}</option>
            </select>
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-200 pt-4 text-xs">
            <span className="text-gray-500">{t('offers.filters.active', 'Filtres actifs')}:</span>
            {activeFilters.map((filter) => (
              <button
                key={filter.key}
                type="button"
                onClick={filter.onClear}
                className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-gray-700 hover:border-indigo-200"
              >
                <span>{filter.label}</span>
                <X className="w-3 h-3 text-gray-400" />
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              {t('offers.filters.clearAll', 'Tout réinitialiser')}
            </button>
          </div>
        )}
      </div>

      {/* Offers List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>{t('common.loading', 'Chargement...')}</span>
          </div>
        </div>
      ) : offers.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <Briefcase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('offers.emptyState.title', 'Aucune offre trouvée')}</h3>
          <p className="text-sm text-gray-600 mb-4">
            {searchTerm || statusFilter !== 'all' 
              ? t('offers.emptyState.filterHint', 'Essayez de modifier vos filtres de recherche.')
              : t('offers.emptyState.createHint', 'Commencez par créer votre première offre d\'emploi.')
            }
          </p>
          {(!searchTerm && statusFilter === 'all') && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              {t('offers.actions.create', 'Créer une offre')}
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
          {offers.map((offer) => (
            <OfferCard key={offer.id} offer={offer} />
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <OfferModal
        show={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setSelectedOffer(null);
        }}
        offer={selectedOffer}
        onSuccess={() => {
          queryClient.invalidateQueries('offers');
          queryClient.invalidateQueries(['offersStats', user?.id]);
          setShowCreateModal(false);
          setShowEditModal(false);
          setSelectedOffer(null);
        }}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal {...confirmProps} />
    </div>
  );
};

// Enhanced Offer Modal Component
const OfferModal = ({ show, onClose, offer, onSuccess }) => {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    title: '',
    client: '',
    location: '',
    duration: '',
    required_skills: '',
    full_description: '',
    status: 'open'
  });
  const [skillTags, setSkillTags] = useState([]);
  const [skillInput, setSkillInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const steps = [
    { id: 1, title: t('offers.modal.steps.basic', 'Informations de base'), icon: FileText },
    { id: 2, title: t('offers.modal.steps.details', 'Détails du poste'), icon: Target },
    { id: 3, title: t('offers.modal.steps.description', 'Description'), icon: Briefcase }
  ];

  useEffect(() => {
    if (offer) {
      setFormData({
        title: offer.title || '',
        client: offer.client || '',
        location: offer.location || '',
        duration: offer.duration || '',
        required_skills: offer.required_skills || '',
        full_description: offer.full_description || '',
        status: offer.status || 'open'
      });
      // Convert skills string to tags
      if (offer.required_skills) {
        setSkillTags(offer.required_skills.split(',').map(skill => skill.trim()));
      }
    } else {
      setFormData({
        title: '',
        client: '',
        location: '',
        duration: '',
        required_skills: '',
        full_description: '',
        status: 'open'
      });
      setSkillTags([]);
    }
    setErrors({});
    setCurrentStep(1);
    setSkillInput('');
  }, [offer, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Skills management
  const addSkill = (skill) => {
    const trimmedSkill = skill.trim();
    if (trimmedSkill && !skillTags.includes(trimmedSkill)) {
      const newSkills = [...skillTags, trimmedSkill];
      setSkillTags(newSkills);
      setFormData(prev => ({
        ...prev,
        required_skills: newSkills.join(', ')
      }));
      setSkillInput('');
      // Clear skills error
      if (errors.required_skills) {
        setErrors(prev => ({
          ...prev,
          required_skills: ''
        }));
      }
    }
  };

  const removeSkill = (skillToRemove) => {
    const newSkills = skillTags.filter(skill => skill !== skillToRemove);
    setSkillTags(newSkills);
    setFormData(prev => ({
      ...prev,
      required_skills: newSkills.join(', ')
    }));
  };

  const handleSkillKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      e.stopPropagation();
      addSkill(skillInput);
    }
  };

  // Prevent form submission on Enter key (except on last step)
  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter' && currentStep < steps.length) {
      e.preventDefault();
      // If we're on a text input that's not the skills input, go to next step
      if (e.target.type === 'text' && e.target.name !== 'skillInput') {
        nextStep(e);
      }
    }
  };

  // Step navigation
  const nextStep = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    }
  };

  const prevStep = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const validateCurrentStep = () => {
    const newErrors = {};
    
    if (currentStep === 1) {
      if (!formData.title.trim()) newErrors.title = t('offers.validation.titleRequired', 'Le titre est requis');
    } else if (currentStep === 2) {
      if (skillTags.length === 0) newErrors.required_skills = t('offers.validation.skillsRequired', 'Au moins une compétence est requise');
    } else if (currentStep === 3) {
      if (!formData.full_description.trim()) newErrors.full_description = t('offers.validation.descriptionRequired', 'La description est requise');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = t('offers.validation.titleRequired', 'Le titre est requis');
    if (skillTags.length === 0) newErrors.required_skills = t('offers.validation.skillsRequired', 'Au moins une compétence est requise');
    if (!formData.full_description.trim()) newErrors.full_description = t('offers.validation.descriptionRequired', 'La description est requise');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Only submit if we're on the last step
    if (currentStep !== steps.length) {
      return;
    }
    
    if (!validate()) return;

    setLoading(true);
    try {
      if (offer) {
        await offersAPI.update(offer.id, formData);
        toast.success(t('offers.messages.updateSuccess', 'Offre modifiée avec succès'));
      } else {
        await offersAPI.create(formData);
        toast.success(t('offers.messages.createSuccess', 'Offre créée avec succès'));
      }
      onSuccess();
    } catch (error) {
      // Use the error translation utility to handle translation interpolation
      const errorMessage = getErrorMessage(
        error, 
        t, 
        offer 
          ? t('offers.errors.updateFailed', 'Erreur lors de la modification') 
          : t('offers.errors.createFailed', 'Erreur lors de la création')
      );
      toast.error(errorMessage);
      console.error('Error saving offer:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="relative w-full max-w-5xl mx-auto bg-white rounded-xl shadow-2xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {offer ? t('offers.modal.editTitle', 'Modifier l\'offre') : t('offers.modal.createTitle', 'Nouvelle offre')}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {t('offers.modal.subtitle', 'Remplissez les informations pour créer une offre d\'emploi attractive')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label={t('common.close')}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;
                
                return (
                  <div key={step.id} className="flex items-center">
                    <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${
                      isActive ? 'bg-primary-100 text-primary-700' :
                      isCompleted ? 'bg-green-100 text-green-700' :
                      'bg-white text-gray-500'
                    }`}>
                      {isCompleted ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                      <span className="text-sm font-medium">{step.title}</span>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`w-8 h-0.5 mx-2 ${
                        isCompleted ? 'bg-green-300' : 'bg-gray-300'
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="p-6">
            <AnimatePresence mode="wait">
              {/* Step 1: Basic Information */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <FileText className="h-6 w-6 text-primary-600" />
                    <h4 className="text-lg font-medium text-gray-900">
                      {t('offers.modal.step1.title', 'Informations de base')}
                    </h4>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('offers.form.title', 'Titre de l\'offre')} *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      className={`input ${errors.title ? 'input-error' : ''}`}
                      placeholder={t('offers.form.titlePlaceholder', 'Ex: Développeur React Senior')}
                    />
                    {errors.title && <p className="text-red-600 text-sm mt-1">{errors.title}</p>}
                    <p className="text-xs text-gray-500 mt-1">
                      <Info className="h-3 w-3 inline mr-1" />
                      {t('offers.form.titleHint', 'Un titre clair et précis attire plus de candidats')}
                    </p>
                  </div>

                  {/* Client & Location */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Building className="h-4 w-4 inline mr-1" />
                        {t('offers.form.client', 'Client')}
                      </label>
                      <input
                        type="text"
                        name="client"
                        value={formData.client}
                        onChange={handleChange}
                        className="input"
                        placeholder={t('offers.form.clientPlaceholder', 'Ex: TechCorp')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <MapPin className="h-4 w-4 inline mr-1" />
                        {t('offers.form.location', 'Localisation')}
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        className="input"
                        placeholder={t('offers.form.locationPlaceholder', 'Ex: Paris, Remote')}
                      />
                    </div>
                  </div>

                  {/* Duration & Status */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Calendar className="h-4 w-4 inline mr-1" />
                        {t('offers.form.duration', 'Durée')}
                      </label>
                      <input
                        type="text"
                        name="duration"
                        value={formData.duration}
                        onChange={handleChange}
                        className="input"
                        placeholder={t('offers.form.durationPlaceholder', 'Ex: 6 mois, CDI')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <CheckCircle className="h-4 w-4 inline mr-1" />
                        {t('offers.form.status', 'Statut')}
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="input"
                      >
                        <option value="open">{t('offers.form.statusOpen', 'Ouverte')}</option>
                        <option value="closed">{t('offers.form.statusClosed', 'Fermée')}</option>
                        <option value="cancelled">{t('offers.form.statusCancelled', 'Annulée')}</option>
                      </select>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Skills & Requirements */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <Target className="h-6 w-6 text-primary-600" />
                    <h4 className="text-lg font-medium text-gray-900">
                      {t('offers.modal.step2.title', 'Compétences et exigences')}
                    </h4>
                  </div>

                  {/* Skills Tags */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('offers.form.requiredSkills', 'Compétences requises')} *
                    </label>
                    
                    {/* Skills Input */}
                    <div className="relative">
                      <input
                        type="text"
                        value={skillInput}
                        onChange={(e) => setSkillInput(e.target.value)}
                        onKeyDown={handleSkillKeyPress}
                        className={`input ${errors.required_skills ? 'input-error' : ''}`}
                        placeholder={t('offers.form.skillsPlaceholder', 'Tapez une compétence et appuyez sur Entrée')}
                      />
                      <button
                        type="button"
                        onClick={() => addSkill(skillInput)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary-600 text-white px-3 py-1 rounded text-sm hover:bg-primary-700 transition-colors"
                      >
                        {t('offers.form.addSkill', 'Ajouter')}
                      </button>
                    </div>

                    {/* Skills Tags Display */}
                    {skillTags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {skillTags.map((skill, index) => (
                          <motion.span
                            key={index}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                          >
                            {skill}
                            <button
                              type="button"
                              onClick={() => removeSkill(skill)}
                              className="ml-2 text-blue-600 hover:text-blue-800"
                              aria-label={t('common.remove')}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </motion.span>
                        ))}
                      </div>
                    )}

                    {errors.required_skills && <p className="text-red-600 text-sm mt-1">{errors.required_skills}</p>}
                    <p className="text-xs text-gray-500 mt-1">
                      <Info className="h-3 w-3 inline mr-1" />
                      {t('offers.form.skillsHint', 'Tapez une compétence et appuyez sur Entrée ou virgule pour l\'ajouter')}
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Description */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <Briefcase className="h-6 w-6 text-primary-600" />
                    <h4 className="text-lg font-medium text-gray-900">
                      {t('offers.modal.step3.title', 'Description du poste')}
                    </h4>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('offers.form.description', 'Description complète')} *
                    </label>
                    <textarea
                      name="full_description"
                      value={formData.full_description}
                      onChange={handleChange}
                      rows={8}
                      className={`input ${errors.full_description ? 'input-error' : ''}`}
                      placeholder={t('offers.form.descriptionPlaceholder', 'Décrivez le poste, les responsabilités, l\'environnement de travail...')}
                    />
                    {errors.full_description && <p className="text-red-600 text-sm mt-1">{errors.full_description}</p>}
                    <p className="text-xs text-gray-500 mt-1">
                      <Info className="h-3 w-3 inline mr-1" />
                      {t('offers.form.descriptionHint', 'Une description détaillée aide les candidats à mieux comprendre le poste')}
                    </p>
                  </div>

                  {/* Preview */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">
                      {t('offers.form.preview', 'Aperçu de l\'offre')}
                    </h5>
                    <div className="text-sm text-gray-600">
                      <h6 className="font-semibold">{formData.title || t('offers.form.titlePlaceholder', 'Titre de l\'offre')}</h6>
                      {formData.client && <p><Building className="h-3 w-3 inline mr-1" />{formData.client}</p>}
                      {formData.location && <p><MapPin className="h-3 w-3 inline mr-1" />{formData.location}</p>}
                      {skillTags.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500">{t('offers.form.requiredSkills', 'Compétences requises')}:</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {skillTags.map((skill, index) => (
                              <span key={index} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center pt-6 mt-6 border-t border-gray-200">
              <div>
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="btn-secondary"
                  >
                    {t('offers.form.previous', 'Précédent')}
                  </button>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary"
                >
                  {t('offers.form.cancel', 'Annuler')}
                </button>

                {currentStep < steps.length ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn-primary"
                  >
                    {t('offers.form.next', 'Suivant')}
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {offer ? t('offers.form.updating', 'Modification...') : t('offers.form.creating', 'Création...')}
                      </>
                    ) : (
                      offer ? t('offers.form.update', 'Modifier') : t('offers.form.create', 'Créer')
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OffersPage;
